import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import Breadcrumb from '../components/breadcrumb';
import { authService } from '../services/auth.service';
import { getGeneralContractorManagementAccess } from '../config/roleAccess';

// Import components
import SearchBar from '../components/contractor-management/SearchBar'; // Reusing existing SearchBar
import GeneralContractorTable from '../components/general-contractor-management/GeneralContractorTable';
import GeneralContractorDialogs from '../components/general-contractor-management/GeneralContractorDialogs';
import GeneralContractorDetailsOverlay from '../components/general-contractor-management/GeneralContractorDetailsOverlay';

// Import types and utilities
import {
  GeneralContractorFormData,
  FormErrors,
  SortConfig,
  ActiveFilters,
  initialFormData
} from '../components/general-contractor-management/types';
import {
  getCoordinatesFromAddress,
  formatFormDataForAPI,
  hasFormDataChanged,
  fetchGeneralContractors
} from '../components/general-contractor-management/utils';
import { validateStep1, validateStep2 } from '../components/general-contractor-management/validation';
import { lookupService } from '../services/lookups.service';
import { GeneralContractorService } from '../services/general-contractor.service';

const GeneralContractorManagement: React.FC = (): JSX.Element => {
  const user = authService.getUser();
  const generalContractorAccess = getGeneralContractorManagementAccess(user?.role || '');

  // Dialog states
  const [showAddGeneralContractorDialog, setShowAddGeneralContractorDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [showGeneralContractorDetails, setShowGeneralContractorDetails] = useState(false);
  const [selectedGeneralContractor, setSelectedGeneralContractor] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [pendingCloseAction, setPendingCloseAction] = useState<null | (() => void)>(null);

  // Refs
  const addGeneralContractorDialogContentRef = useRef<HTMLDivElement>(null);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);

  // Pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [currentStep, setCurrentStep] = useState(1);
  const pageSize = 10;
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'name',
    direction: null
  });
  const [totalItems, setTotalItems] = useState(0);

  // Data states
  const [generalContractors, setGeneralContractors] = useState<any[]>([]);
  const [formData, setFormData] = useState<GeneralContractorFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({
    name: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
  });

  // Filter states
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    status: [],
    generalContractor: [],
    search: ''
  });
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Province options
  const [provinceOptions, setProvinceOptions] = useState<any[]>([]);

  // Deactivation eligibility
  const [canDeactivate, setCanDeactivate] = useState<boolean>(true);
  const [deactivationReason, setDeactivationReason] = useState<string>('');

  // Function to reset form data to initial state
  const resetFormData = () => {
    setFormData(initialFormData);
    setCurrentStep(1);
    setErrors({
      name: '',
      address: '',
      city: '',
      province: '',
      postalCode: '',
    });
  };

  const handleAddGeneralContractor = () => {
    resetFormData();
    setEditFormData(null);  // Clear any existing edit data
    setCurrentStep(1);
    setShowAddGeneralContractorDialog(true);
  };

  // Handle row click
  const handleRowClick = (item: any) => {
    // Ensure we're handling the data properly
    if (!item || !item.id) return;

    // Store the selected general contractor and show details
    setSelectedGeneralContractor(item);
    setShowGeneralContractorDetails(true);
    setIsEditMode(false);
  };

  // Handle sorting
  const handleSort = (key: string, direction: 'asc' | 'desc' | null) => {
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Create a wrapper for setCurrentPage
  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search with immediate UI update
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const onModalClose = () => {
    setShowGeneralContractorDetails(false);
    // Don't clear editFormData here - keep it for the edit modal
  }

  // Handle debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeFilters.search !== searchTerm) {
        const newFilters = {
          ...activeFilters,
          search: searchTerm
        };
        setActiveFilters(newFilters);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Load provinces on mount
  useEffect(() => {
    const loadProvinces = async () => {
      try {
        setIsLoadingProvinces(true);
        const provinces = await lookupService.getcontractorProvinces();
        setProvinceOptions(provinces);
      } catch (error) {
        toast.error('Error loading provinces');
      } finally {
        setIsLoadingProvinces(false);
      }
    };
    loadProvinces();
  }, []);

  // Initialize formData when editing (when editFormData is set)
  useEffect(() => {
    if (editFormData && showAddGeneralContractorDialog) {
      setFormData({
        id: editFormData.id || '',
        name: editFormData.name || '',
        active: editFormData.active ?? true,
        address: editFormData.address || '',
        city: editFormData.city || '',
        province: editFormData.province || '',
        postalCode: editFormData.postalCode || '',
        coordinates: editFormData.coordinates || null
      });
    } else if (!editFormData && showAddGeneralContractorDialog) {
      // Reset to initial form data when adding new
      resetFormData();
    }
  }, [editFormData, showAddGeneralContractorDialog]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;

    // Validation for contractor name and owner name
    if (name === 'name') {
      // Only allow alphanumeric and spaces, max 50 characters
      const validValue = value.replace(/[^a-zA-Z0-9\s]/g, '').slice(0, 50);
      setFormData(prev => ({ ...prev, [name]: validValue }));
      // Clear error if valid
      if (/^[a-zA-Z0-9\s]{1,50}$/.test(validValue)) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Clear errors when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // State to track if we've already shown the similar name warning
  const [hasShownSimilarNameWarning, setHasShownSimilarNameWarning] = useState(false);

  // Navigation functions
  const handleNext = () => {
    if (currentStep === 1) {
      const isValid = validateStep1(formData, setErrors);

      if (isValid) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      setShowSaveConfirmDialog(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Save confirmation handlers
  const confirmSave = async (ignoreSimilarNameWarning: boolean = false) => {

    try {
      setIsSubmitting(true);
      setShowSaveConfirmDialog(false);

      const isValid = validateStep2(formData, setErrors, provinceOptions);

      if (!isValid) {
        setIsSubmitting(false);
        return;
      }

      // Attempt to fetch coordinates if we have address data and coordinates not already set
      // This is a best-effort attempt - if it fails, we still save the address without coordinates
      const hasAddressData = formData.address?.trim() && formData.city?.trim();

      if (hasAddressData && !formData.coordinates) {
        try {
          const coords = await getCoordinatesFromAddress(
            formData.address,
            formData.city,
            formData.province || '',
            formData.postalCode || ''
          );
          if (coords) {
            formData.coordinates = coords;
          }
          // If coords is null, that's fine - we'll save without coordinates
        } catch (error) {
          // Log but don't block save if geocoding fails
          console.warn('Geocoding failed, saving address without coordinates:', error);
          // Continue with save - coordinates will be null
        }
      }

      // Format the data for API submission
      const apiData = formatFormDataForAPI(formData);
      // If we've already shown the warning, add the flag to bypass it
      if (hasShownSimilarNameWarning) {
        apiData.ignoreSimilarNameWarning = true;
      }

      if (editFormData?.id) {
        // Check if any changes were made
        if (!hasFormDataChanged(editFormData, formData)) {
          toast.info('No changes detected');
          setShowSaveConfirmDialog(false);
          setShowAddGeneralContractorDialog(false);
          setEditFormData(null);
          resetFormData();
          setCurrentStep(1);
          return;
        }

        // Update existing general contractor
        await GeneralContractorService.updateGeneralContractor(editFormData.id, apiData);
        toast.success('General contractor updated successfully');
      } else {
        // Create new general contractor
        await GeneralContractorService.createGeneralContractor(apiData);
        toast.success('General contractor added successfully');
      }

      setShowSaveConfirmDialog(false);
      setShowAddGeneralContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setCurrentStep(1);
      setHasShownSimilarNameWarning(false); // Reset the warning flag

      // Refresh the data
      fetchGeneralContractors(
        { currentPage, searchTerm: activeFilters.search, pageSize, sortConfig },
        { setLoading, setGeneralContractors, setTotalItems }
      );

    } catch (error: any) {
      console.log('=== Error caught ===');
      console.log('error:', error);
      console.log('error.message:', error.message);
      console.log('error.response?.data:', error.response?.data);

      // Get the error message from either error.message or error.response.data.message
      const errorMessage = error.message || error.response?.data?.message || '';
      console.log('errorMessage:', errorMessage);

      // Check if this is a similar name warning (not a hard error)
      if (errorMessage.includes('similar name already exists') && !hasShownSimilarNameWarning) {
        console.log('Similar name warning detected - blocking creation');
        const existingName = errorMessage.match(/"([^"]+)"/)?.[1] || 'another contractor';

        // Mark that we've shown the warning
        setHasShownSimilarNameWarning(true);

        // Show warning toast and do NOT proceed
        toast.warning(
          `A contractor with a similar name already exists: "${existingName}". Click "Add General Contractor" again to proceed.`,
          { autoClose: 5000 }
        );

        // Re-open the save confirmation dialog so user can click again
        setShowSaveConfirmDialog(true);
      } else {
        // Regular error - show error toast
        console.log('Regular error, showing toast');
        toast.error(error.response?.data?.message || error.message || 'Error saving general contractor');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSave = () => {
    setShowSaveConfirmDialog(false);
  };

  // Block/Unblock handlers
  const confirmBlock = () => {
    setShowBlockDialog(false);
    // TODO: Implement block functionality
  };

  // Close dialog handlers
  const handleCloseAddGeneralContractorDialog = () => {
    // If on step 1 and not editing, just close
    if (!editFormData && currentStep === 1 && !hasFormDataChanged(initialFormData, formData)) {
      setShowAddGeneralContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setCurrentStep(1);
      setHasShownSimilarNameWarning(false);
      return;
    }
    // If editing, compare with editFormData
    if (editFormData && !hasFormDataChanged(editFormData, formData)) {
      setShowAddGeneralContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setCurrentStep(1);
      setHasShownSimilarNameWarning(false);
      return;
    }
    // Otherwise, show confirmation dialog
    setShowCloseConfirmDialog(true);
    setPendingCloseAction(() => () => {
      setShowAddGeneralContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setCurrentStep(1);
      setShowCloseConfirmDialog(false);
      setHasShownSimilarNameWarning(false);
    });
  };

  // Details overlay handlers
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleSaveEdit = async (options?: { toastShown?: boolean }) => {
    setIsSubmitting(true);
    try {
      // Only show toast if not already shown in the child component
      if (!options?.toastShown) {
        toast.success('General contractor updated successfully');
      }

      // Update UI state
      setIsEditMode(false);
      setShowGeneralContractorDetails(false);

      // Refresh the general contractors list with current page and search terms
      await fetchGeneralContractors(
        { currentPage, searchTerm: activeFilters.search, pageSize, sortConfig },
        { setLoading, setGeneralContractors, setTotalItems }
      );
    } catch (error: any) {
      toast.error(error.message || 'Error updating general contractor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> |
    { target: { name: string; value: any } }
  ) => {
    const { name, value } = e.target;
    setEditFormData((prev: any) => ({
      ...prev,
      [name]: value
    }));
  };

  // Initial load of general contractors
  useEffect(() => {
    fetchGeneralContractors(
      { currentPage: 1, searchTerm: activeFilters.search, pageSize, sortConfig },
      { setLoading, setGeneralContractors: setGeneralContractors, setTotalItems }
    );
  }, []);

  // Handle search and filter changes
  useEffect(() => {
    fetchGeneralContractors(
      { currentPage, searchTerm: activeFilters.search, pageSize, sortConfig },
      { setLoading, setGeneralContractors: setGeneralContractors, setTotalItems }
    );
  }, [sortConfig, activeFilters, currentPage]);

  // Check deactivation eligibility when editing a general contractor
  useEffect(() => {
    const checkEligibility = async () => {
      if (editFormData?.id && editFormData.active) {
        try {
          const eligibility = await GeneralContractorService.checkDeactivationEligibility(editFormData.id);
          setCanDeactivate(eligibility.canDeactivate);
          setDeactivationReason(eligibility.reason || '');
        } catch (error) {
          console.error('Error checking deactivation eligibility:', error);
          setCanDeactivate(true);
          setDeactivationReason('');
        }
      } else {
        setCanDeactivate(true);
        setDeactivationReason('');
      }
    };

    if (showAddGeneralContractorDialog) {
      checkEligibility();
    }
  }, [editFormData, showAddGeneralContractorDialog]);

  return (
    <>
      <div id="general-contractor-management-container" className="bg-gray-50 p-3 sm:p-4 md:p-6">
        <div className="">
          <div className="mb-4 sm:mb-6">
            <Breadcrumb title="General Contractor Management" />
          </div>

          <div className="bg-white rounded-card shadow overflow-hidden p-3 sm:p-4 mb-4">
            <div className="flex items-center gap-2 w-full sm:w-auto justify-between">
              <SearchBar
                searchTerm={searchTerm}
                handleSearch={handleSearch}
                placeholderText={"Search by Name and Status"}
              />
              <div className="flex items-center gap-2">
                {generalContractorAccess.addGeneralContractor && (
                  <button
                    id="btn-add-general-contractor"
                    className="btn-primary text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px]"
                    onClick={handleAddGeneralContractor}
                  >
                    <span className=""> + </span>
                    <span className="ml-1 hidden sm:inline">
                      Add General Contractor
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <GeneralContractorTable
            loading={loading}
            generalContractors={generalContractors}
            totalItems={totalItems}
            currentPage={currentPage}
            pageSize={pageSize}
            sortConfig={sortConfig}
            searchTerm={searchTerm}
            handleSearch={handleSearch}
            handleRowClick={handleRowClick}
            handleSort={handleSort}
            setCurrentPage={handleSetCurrentPage}
          />

          {/* General Contractor Dialogs */}
          <GeneralContractorDialogs
            showAddGeneralContractorDialog={showAddGeneralContractorDialog}
            showBlockDialog={showBlockDialog}
            showSaveConfirmDialog={showSaveConfirmDialog}
            showCloseConfirmDialog={showCloseConfirmDialog}
            isSubmitting={isSubmitting}
            formData={formData}
            errors={errors}
            currentStep={currentStep}
            editFormData={editFormData}
            addGeneralContractorDialogContentRef={addGeneralContractorDialogContentRef}
            provinceOptions={provinceOptions}
            isLoadingProvinces={isLoadingProvinces}
            canDeactivate={canDeactivate}
            deactivationReason={deactivationReason}
            handleCloseAddGeneralContractorDialog={handleCloseAddGeneralContractorDialog}
            handleInputChange={handleInputChange}
            handleBack={handleBack}
            handleNext={handleNext}
            handleCancelSave={handleCancelSave}
            confirmSave={confirmSave}
            confirmBlock={confirmBlock}
            setShowBlockDialog={setShowBlockDialog}
            setShowCloseConfirmDialog={setShowCloseConfirmDialog}
            pendingCloseAction={pendingCloseAction}
            setFormData={setFormData}
          />

          {/* Add GeneralContractorDetailsOverlay */}
          {showGeneralContractorDetails && (
            <GeneralContractorDetailsOverlay
              isOpen={showGeneralContractorDetails}
              onClose={() => onModalClose()}
              generalContractorId={selectedGeneralContractor?.id || ''}
              isEditMode={isEditMode}
              onEditClick={handleEditClick}
              onSaveEdit={handleSaveEdit}
              editFormData={editFormData}
              onEditInputChange={handleEditInputChange}
              onShowBlockDialog={() => setShowBlockDialog(true)}
              setSelectedGeneralContractor={setSelectedGeneralContractor}
              setEditFormData={setEditFormData}
              setShowAddGeneralContractorDialog={setShowAddGeneralContractorDialog}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default GeneralContractorManagement;